'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  detectRegion,
  hasConsented,
  acceptAll,
  rejectAll,
  ConsentRegion,
} from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [region, setRegion] = useState<ConsentRegion>('GDPR');
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    if (hasConsented()) return;
    detectRegion()?.then((r) => {
      setRegion(r);
      if (r !== 'NONE') setVisible(true);
    });
  }, []);

  const handleAcceptAll = useCallback(() => {
    acceptAll(region);
    setVisible(false);
  }, [region]);

  const handleRejectAll = useCallback(() => {
    rejectAll(region);
    setVisible(false);
  }, [region]);

  const handlePreferencesSaved = useCallback(() => {
    setShowPreferences(false);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <>
      {showPreferences && (
        <ConsentPreferenceCenter
          region={region}
          onSave={handlePreferencesSaved}
          onClose={() => setShowPreferences(false)}
        />
      )}

      <div
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl px-4 py-4 sm:px-6"
      >
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-700 leading-relaxed">
              {region === 'GDPR' ? (
                <>
                  We use cookies and similar technologies to operate our platform, analyse usage, and
                  improve your experience. By clicking <strong>Accept All</strong>, you consent to our
                  use of cookies as described in our{' '}
                  <a href="/cookies" className="text-primary underline hover:text-primary-dark">
                    Cookie Policy
                  </a>
                  . You can manage your preferences at any time.
                </>
              ) : (
                <>
                  We collect personal information including usage data and cookies. California residents
                  have the right to opt out of the sale or sharing of personal information. See our{' '}
                  <a href="/privacy" className="text-primary underline hover:text-primary-dark">
                    Privacy Policy
                  </a>{' '}
                  for details.
                </>
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowPreferences(true)}
              className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2 transition-colors px-1"
            >
              Manage Preferences
            </button>
            <button
              onClick={handleRejectAll}
              className="text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 hover:border-gray-400 px-3 py-1.5 rounded-lg transition-colors"
            >
              {region === 'CCPA' ? 'Opt Out' : 'Reject All'}
            </button>
            <button
              onClick={handleAcceptAll}
              className="text-xs font-semibold text-white bg-primary hover:bg-primary-dark px-4 py-1.5 rounded-lg transition-colors shadow-sm"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
